import { RefObject, useRef } from "react";
import { useReactiveVar } from "@apollo/client";
import {
  ApplicationBundleTypeEnum,
  ApplicationMainModelFileTypeEnum,
  ContentTypeEnum,
  ExperimentBundleSubType,
  LanguagesEnum,
} from "../../../../../generated/graphql";
import { mobileAppModel } from "../../state";
import { currentBundleFindHandler, getBundleVariant } from "../../utils";
import { BundleNames, PLATFORM_TO_BUNDLE_MAP } from "../../constants";
import {
  CurrentBundleContainer,
  MobileAppModalBundleName,
} from "../../../MobileAppModal.style";
import { Box, Collapse, IconButton } from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import {
  BundleLink,
  ButtonCustom,
  ImportFileUpload,
} from "../../../../shared/Style/Style";
import { CurrentBundleType } from "../../types";
import { AppModelColoringFile } from "../AppModelColoringFile";

export const AppModelBundles = () => {
  const bundlePhotoInput = useRef<HTMLInputElement | null>(null);
  const iosInput = useRef<HTMLInputElement | null>(null);
  const iosInputOptimized = useRef<HTMLInputElement | null>(null);
  const webGLInput = useRef<HTMLInputElement | null>(null);
  const webGLInputOptimized = useRef<HTMLInputElement | null>(null);
  const androidInput = useRef<HTMLInputElement | null>(null);
  const androidInputOptimized = useRef<HTMLInputElement | null>(null);
  const vrExperimentInput = useRef<HTMLInputElement | null>(null);
  const androidBoardInput = useRef<HTMLInputElement | null>(null);
  const windowsBoardInput = useRef<HTMLInputElement | null>(null);
  const manifestInputIos = useRef<HTMLInputElement | null>(null);
  const manifestInputIosOptimized = useRef<HTMLInputElement | null>(null);
  const manifestInputWebGL = useRef<HTMLInputElement | null>(null);
  const manifestInputWebGLOptimized = useRef<HTMLInputElement | null>(null);
  const manifestInputAndroid = useRef<HTMLInputElement | null>(null);
  const manifestInputAndroidOptimized = useRef<HTMLInputElement | null>(null);
  const manifestInputVr = useRef<HTMLInputElement | null>(null);
  const manifestInputAndroidBoard = useRef<HTMLInputElement | null>(null);
  const manifestInputWindowsBoard = useRef<HTMLInputElement | null>(null);
  const resourceInputIos = useRef<HTMLInputElement | null>(null);
  const resourceInputAndroid = useRef<HTMLInputElement | null>(null);
  const resourceInputVr = useRef<HTMLInputElement | null>(null);
  const resourceInputWebGl = useRef<HTMLInputElement | null>(null);
  const resourceInputAndroidBoard = useRef<HTMLInputElement | null>(null);
  const resourceInputWindowsBoard = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInputIos = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInputAndroid = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInputWebGl = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInputVr = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInputAndroidBoard = useRef<HTMLInputElement | null>(
    null
  );
  const resourcesManifestInputWindowsBoard = useRef<HTMLInputElement | null>(
    null
  );
  const isLoading = useReactiveVar(mobileAppModel.updateLoading);
  const model = useReactiveVar(mobileAppModel.model);
  const activePlatform = useReactiveVar(mobileAppModel.activePlatform);

  if (!model) return null;

  const {
    id,
    bundles,
    bundleType,
    bundleLink,
    lang,
    bundlePhoto,
    contentType,
  } = model;
  const actualLang = lang || LanguagesEnum.Ukr;

  const currentBundleIosOptimized = currentBundleFindHandler(
    bundles,
    ApplicationMainModelFileTypeEnum.IosOptimized,
    actualLang
  );
  const currentBundleWebGLOptimized = currentBundleFindHandler(
    bundles,
    ApplicationMainModelFileTypeEnum.WebGlOptimized,
    actualLang
  );
  const currentBundleAndroidOptimized = currentBundleFindHandler(
    bundles,
    ApplicationMainModelFileTypeEnum.AndroidOptimized,
    actualLang
  );
  const currentBundleIos = currentBundleFindHandler(
    bundles,
    ApplicationMainModelFileTypeEnum.Ios,
    actualLang
  );
  const currentBundleWebGL = currentBundleFindHandler(
    bundles,
    ApplicationMainModelFileTypeEnum.WebGl,
    actualLang
  );
  const currentBundleAndroid = currentBundleFindHandler(
    bundles,
    ApplicationMainModelFileTypeEnum.Android,
    actualLang
  );
  const currentBundleVr = currentBundleFindHandler(
    bundles,
    ApplicationMainModelFileTypeEnum.Vr,
    actualLang
  );
  const currentBundleAndroidBoard = currentBundleFindHandler(
    bundles,
    ApplicationMainModelFileTypeEnum.InteractiveWhiteboardAndroid,
    actualLang
  );
  const currentBundleWindowsBoard = currentBundleFindHandler(
    bundles,
    ApplicationMainModelFileTypeEnum.InteractiveWhiteboardWindows,
    actualLang
  );

  const bundlesData = [
    {
      name: BundleNames.Ios,
      currentBundle: currentBundleIos,
      type: ExperimentBundleSubType.Bundle,
      inputRef: iosInput,
      handleBundle: (file: File) =>
        mobileAppModel.createBundleFile({
          modelId: id,
          type: ApplicationMainModelFileTypeEnum.Ios,
          file,
        }),
    },
    {
      name: BundleNames.IosOptimized,
      currentBundle: currentBundleIosOptimized,
      type: ExperimentBundleSubType.Bundle,
      inputRef: iosInputOptimized,
      handleBundle: (file: File) =>
        mobileAppModel.createBundleFile({
          modelId: id,
          type: ApplicationMainModelFileTypeEnum.IosOptimized,
          file,
        }),
    },
    {
      name: BundleNames.WebGL,
      currentBundle: currentBundleWebGL,
      type: ExperimentBundleSubType.Bundle,
      inputRef: webGLInput,
      handleBundle: (file: File) =>
        mobileAppModel.createBundleFile({
          modelId: id,
          type: ApplicationMainModelFileTypeEnum.WebGl,
          file,
        }),
    },
    {
      name: BundleNames.WebGlOptimized,
      currentBundle: currentBundleWebGLOptimized,
      type: ExperimentBundleSubType.Bundle,
      inputRef: webGLInputOptimized,
      handleBundle: (file: File) =>
        mobileAppModel.createBundleFile({
          modelId: id,
          type: ApplicationMainModelFileTypeEnum.WebGlOptimized,
          file,
        }),
    },
    {
      name: BundleNames.Android,
      currentBundle: currentBundleAndroid,
      type: ExperimentBundleSubType.Bundle,
      inputRef: androidInput,
      handleBundle: (file: File) =>
        mobileAppModel.createBundleFile({
          modelId: id,
          type: ApplicationMainModelFileTypeEnum.Android,
          file,
        }),
    },
    {
      name: BundleNames.AndroidOptimized,
      currentBundle: currentBundleAndroidOptimized,
      type: ExperimentBundleSubType.Bundle,
      inputRef: androidInputOptimized,
      handleBundle: (file: File) =>
        mobileAppModel.createBundleFile({
          modelId: id,
          type: ApplicationMainModelFileTypeEnum.AndroidOptimized,
          file,
        }),
    },
    {
      name: BundleNames.Vr,
      currentBundle: currentBundleVr,
      type: ExperimentBundleSubType.Bundle,
      inputRef: vrExperimentInput,
      handleBundle: (file: File) =>
        mobileAppModel.createBundleFile({
          modelId: id,
          type: ApplicationMainModelFileTypeEnum.Vr,
          file,
        }),
    },
    {
      name: BundleNames.AndroidBoardBundle,
      currentBundle: currentBundleAndroidBoard,
      type: ExperimentBundleSubType.Bundle,
      inputRef: androidBoardInput,
      handleBundle: (file: File) =>
        mobileAppModel.createBundleFile({
          modelId: id,
          type: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardAndroid,
          file,
        }),
    },
    {
      name: BundleNames.WindowsBoardBundle,
      currentBundle: currentBundleWindowsBoard,
      type: ExperimentBundleSubType.Bundle,
      inputRef: windowsBoardInput,
      handleBundle: (file: File) =>
        mobileAppModel.createBundleFile({
          modelId: id,
          type: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardWindows,
          file,
        }),
    },
    {
      name: BundleNames.IosManifest,
      currentBundle: currentBundleIos,
      type: ExperimentBundleSubType.Manifest,
      inputRef: manifestInputIos,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleIos?.id,
          manifest: file,
        }),
    },
    {
      name: BundleNames.IosOptimizedManifest,
      currentBundle: currentBundleIosOptimized,
      type: ExperimentBundleSubType.Manifest,
      inputRef: manifestInputIosOptimized,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleIosOptimized?.id,
          manifest: file,
        }),
    },
    {
      name: BundleNames.WebGlManifest,
      currentBundle: currentBundleWebGL,
      type: ExperimentBundleSubType.Manifest,
      inputRef: manifestInputWebGL,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleWebGL?.id,
          manifest: file,
        }),
    },
    {
      name: BundleNames.WebGlOptimizedManifest,
      currentBundle: currentBundleWebGLOptimized,
      type: ExperimentBundleSubType.Manifest,
      inputRef: manifestInputWebGLOptimized,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleWebGLOptimized?.id,
          manifest: file,
        }),
    },
    {
      name: BundleNames.AndroidManifest,
      currentBundle: currentBundleAndroid,
      type: ExperimentBundleSubType.Manifest,
      inputRef: manifestInputAndroid,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleAndroid?.id,
          manifest: file,
        }),
    },
    {
      name: BundleNames.AndroidOptimizedManifest,
      currentBundle: currentBundleAndroidOptimized,
      type: ExperimentBundleSubType.Manifest,
      inputRef: manifestInputAndroidOptimized,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleAndroidOptimized?.id,
          manifest: file,
        }),
    },
    {
      name: BundleNames.VrManifest,
      currentBundle: currentBundleVr,
      type: ExperimentBundleSubType.Manifest,
      inputRef: manifestInputVr,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleVr?.id,
          manifest: file,
        }),
    },
    {
      name: BundleNames.AndroidBoardManifest,
      currentBundle: currentBundleAndroidBoard,
      type: ExperimentBundleSubType.Manifest,
      inputRef: manifestInputAndroidBoard,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleAndroidBoard?.id,
          manifest: file,
        }),
    },
    {
      name: BundleNames.WindowsBoardManifest,
      currentBundle: currentBundleWindowsBoard,
      type: ExperimentBundleSubType.Manifest,
      inputRef: manifestInputWindowsBoard,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleWindowsBoard?.id,
          manifest: file,
        }),
    },
    {
      name: BundleNames.IosResources,
      currentBundle: currentBundleIos,
      type: ExperimentBundleSubType.Resources,
      inputRef: resourceInputIos,
      handleBundle: (file: File) => {
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleIos?.id,
          resources: file,
        });
      },
    },
    {
      name: BundleNames.IosResourcesManifest,
      currentBundle: currentBundleIos,
      type: ExperimentBundleSubType.ResourcesManifest,
      inputRef: resourcesManifestInputIos,
      handleBundle: (file: File) => {
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleIos?.id,
          resourcesManifest: file,
        });
      },
    },
    {
      name: BundleNames.AndroidResources,
      currentBundle: currentBundleAndroid,
      type: ExperimentBundleSubType.Resources,
      inputRef: resourceInputAndroid,
      handleBundle: (file: File) => {
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleAndroid?.id,
          resources: file,
        });
      },
    },
    {
      name: BundleNames.AndroidResourcesManifest,
      currentBundle: currentBundleAndroid,
      type: ExperimentBundleSubType.ResourcesManifest,
      inputRef: resourcesManifestInputAndroid,
      handleBundle: (file: File) => {
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleAndroid?.id,
          resourcesManifest: file,
        });
      },
    },
    {
      name: BundleNames.WebGlResources,
      currentBundle: currentBundleWebGL,
      type: ExperimentBundleSubType.Resources,
      inputRef: resourceInputWebGl,
      handleBundle: (file: File) => {
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleWebGL?.id,
          resources: file,
        });
      },
    },
    {
      name: BundleNames.WebGlResourceManifests,
      currentBundle: currentBundleWebGL,
      type: ExperimentBundleSubType.ResourcesManifest,
      inputRef: resourcesManifestInputWebGl,
      handleBundle: (file: File) => {
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleWebGL?.id,
          resourcesManifest: file,
        });
      },
    },
    {
      name: BundleNames.VrResources,
      currentBundle: currentBundleVr,
      type: ExperimentBundleSubType.Resources,
      inputRef: resourceInputVr,
      handleBundle: (file: File) => {
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleVr?.id,
          resources: file,
        });
      },
    },
    {
      name: BundleNames.VrResourcesManifest,
      currentBundle: currentBundleVr,
      type: ExperimentBundleSubType.ResourcesManifest,
      inputRef: resourcesManifestInputVr,
      handleBundle: (file: File) => {
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleVr?.id,
          resourcesManifest: file,
        });
      },
    },
    {
      name: BundleNames.AndroidBoardResources,
      currentBundle: currentBundleAndroidBoard,
      type: ExperimentBundleSubType.Resources,
      inputRef: resourceInputAndroidBoard,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleAndroidBoard?.id,
          resources: file,
        }),
    },
    {
      name: BundleNames.WindowsBoardResources,
      currentBundle: currentBundleWindowsBoard,
      type: ExperimentBundleSubType.Resources,
      inputRef: resourceInputWindowsBoard,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleWindowsBoard?.id,
          resources: file,
        }),
    },
    {
      name: BundleNames.AndroidBoardResourcesManifest,
      currentBundle: currentBundleAndroidBoard,
      type: ExperimentBundleSubType.ResourcesManifest,
      inputRef: resourcesManifestInputAndroidBoard,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleAndroidBoard?.id,
          resourcesManifest: file,
        }),
    },
    {
      name: BundleNames.WindowsBoardResourcesManifest,
      currentBundle: currentBundleWindowsBoard,
      type: ExperimentBundleSubType.ResourcesManifest,
      inputRef: resourcesManifestInputWindowsBoard,
      handleBundle: (file: File) =>
        mobileAppModel.updateModalFile({
          modelId: id,
          id: currentBundleWindowsBoard?.id,
          resourcesManifest: file,
        }),
    },
  ];

  const visibleBundles = PLATFORM_TO_BUNDLE_MAP[activePlatform] || [];

  const filteredBundlesData = bundlesData.filter((bundle) =>
    visibleBundles.includes(bundle.name)
  );

  const renderCurrentBundle = (
    name: BundleNames,
    currentBundle: CurrentBundleType,
    type: ExperimentBundleSubType,
    inputRef: RefObject<HTMLInputElement>,
    handleBundle: (file: File) => void
  ) => {
    const bundleVariant = getBundleVariant(currentBundle, type);

    const isDisabledInput =
      isLoading ||
      !contentType?.name ||
      (type !== ExperimentBundleSubType.Bundle && !currentBundle);

    const subType = type === ExperimentBundleSubType.Bundle ? undefined : type;

    return (
      <CurrentBundleContainer key={name}>
        <MobileAppModalBundleName>{name}</MobileAppModalBundleName>

        {!!bundleVariant ? (
          <span>
            <BundleLink href={bundleVariant}>{name}</BundleLink>

            <IconButton
              disabled={isLoading}
              onClick={() =>
                mobileAppModel.deleteBundleFile(id, currentBundle?.id, subType)
              }
            >
              <Delete color="secondary" />
            </IconButton>
          </span>
        ) : (
          <ButtonCustom
            disabled={isDisabledInput}
            onClick={() => inputRef.current?.click()}
          >
            <span>Прикріпити</span>
            <ImportFileUpload
              type="file"
              ref={inputRef}
              onChange={({ target: { files } }) =>
                files?.[0] && handleBundle(files[0])
              }
            />
          </ButtonCustom>
        )}
      </CurrentBundleContainer>
    );
  };

  const bundleTypeHandler = (value: { type: ApplicationBundleTypeEnum }) => {
    mobileAppModel.setBundleType(value.type);
  };

  return (
    <>
      {/*
            //TODO: temporarily unavailable components AD-184

            <CustomRadioGroup
                disabled={isLoading}
                items={bundleTypeItems}
                chosenItem={bundleType}
                state={{ type: bundleType }}
                setNewState={bundleTypeHandler}
                fieldName="type"
                horizontal={true}
            />

            <Collapse
                in={bundleType === ApplicationBundleTypeEnum.BundleLink}
                collapsedSize="0px"
            >
                <ModalWithPhotoInput
                    disabled={isLoading}
                    fullWidth
                    label="Ссылка"
                    value={bundleLink}
                    onChange={({ target: { value } }) => mobileAppModel.setBundleLink(value)}
                    defaultValue={bundleLink || ""}
                    InputProps={{
                        endAdornment: (
                            <SaveIconButton
                                disabled={isLoading}
                                save={() => mobileAppModel.updateBundleLink(id, bundleLink)}
                            />
                        ),
                    }}
                />
            </Collapse>

            <Collapse
                in={bundleType === ApplicationBundleTypeEnum.Photo}
                collapsedSize="0px"
            >
                <Box display="flex" justifyContent="flex-end" alignItems="center">
                    {bundlePhoto ? (
                        <span>
                            <PrimaryLink href={bundlePhoto}>Картинка</PrimaryLink>

                            <IconButton
                                disabled={isLoading}
                                onClick={() => mobileAppModel.deleteAppModelFiles( id, ModelFileTypeEnum.BundlePhoto )}
                            >
                                <Delete color="secondary" />
                            </IconButton>
                        </span>
                    ) : (
                            <ButtonCustom
                                onClick={() => bundlePhotoInput.current.click()}
                                disabled={isLoading}
                            >
                            <span>Прикріпити</span>
                            <ImportFileUpload
                                type="file"
                                accept="image/png, image/jpeg"
                                ref={bundlePhotoInput}
                                onChange={({ target: { files } }) =>
                                  files[0] && mobileAppModel.updateBundlePhoto(id, files[0])
                                }
                            />
                        </ButtonCustom>
                    )}
                </Box>
            </Collapse> */}

      <Collapse
        in={
          bundleType === ApplicationBundleTypeEnum.Bundle && !!contentType?.name
        }
        collapsedSize="0px"
      >
        {filteredBundlesData.map(
          ({ name, currentBundle, type, inputRef, handleBundle }) =>
            renderCurrentBundle(
              name,
              currentBundle,
              type,
              inputRef,
              handleBundle
            )
        )}
        {activePlatform === ContentTypeEnum.ColoringPageAr && (
          <AppModelColoringFile />
        )}
      </Collapse>
    </>
  );
};
